import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'
import { canUseStatistics, canUseMarketing } from './consent'

const DEPLOYMENT_KEY = '3ef674a74b1c64de3b599b9d74000523'

let client: ExperimentClient | null = null
let fetchPromise: Promise<void> | null = null
let hasFetched = false

function getClient(): ExperimentClient {
  if (!client) {
    client = Experiment.initializeWithAmplitudeAnalytics(DEPLOYMENT_KEY)
  }
  return client
}

function hasConsent(): boolean {
  return canUseStatistics() || canUseMarketing()
}

/**
 * Fetches experiment variants from the server.
 * Skips if already successfully fetched and `force` is false.
 * When consent is not yet given, the Amplitude SDK is opted-out and the
 * Experiment SDK may not be able to record exposures — call `refetchIfNeeded`
 * after consent changes to fix this.
 */
async function doFetch(force = false): Promise<void> {
  if (hasFetched && !force) return
  if (fetchPromise && !force) return fetchPromise

  const promise = getClient()
    .fetch()
    .then(() => {
      hasFetched = true
    })
    .catch(err => {
      console.warn('Experiment fetch failed:', err)
    })
    .finally(() => {
      if (fetchPromise === promise) {
        fetchPromise = null
      }
    })

  fetchPromise = promise
  return promise
}

/**
 * Returns the variant value for the given flag key.
 * Ensures a single fetch has completed before reading.
 * Returns `undefined` if the fetch fails or the flag is not found.
 */
export async function getExperimentVariant(flagKey: string): Promise<string | undefined> {
  await doFetch()
  return getClient().variant(flagKey).value
}

/**
 * Call after cookie consent changes (CookiebotOnAccept) or when we determine
 * consent is not required (non-EU fallback).
 * If variants were already fetched while Amplitude was opted-out, re-fetches
 * so exposures are recorded. If a fetch is still in flight, waits for it
 * to complete then re-fetches.
 */
export async function refetchOnConsent(): Promise<void> {
  if (!hasConsent()) return

  if (fetchPromise) {
    await fetchPromise
  }

  if (hasFetched) {
    doFetch(true)
  }
}
